// eslint-disable-next-line import/no-unresolved
import { Auth } from 'auth/services/Auth';
import axios, { AxiosInstance } from 'axios';
import { StatusCodes } from 'http-status-codes';

import { BaseTypes, getAPIUrl } from '@utils/URLs';

const auth = Auth.getInstance();
export const AllClients: AxiosInstance[] = [];

let isRefreshing = false;
const refreshSubscribers: { (token: string): void }[] = [];

// Stack of requests that need to be retried once a new Auth Token has been provided
const subscribeTokenRefresh = (cb: (token: string) => void) => {
    refreshSubscribers.push(cb);
};

// Execute each request that needed to be retried due to expired Auth Token.
const onRrefreshed = (token: string) => {
    refreshSubscribers.map((cb) => cb(token));
};

interface AxiosClientOptions {
    baseType?: BaseTypes;
}

const axiosClient = ({
    baseType = BaseTypes.DIRECT,
}: AxiosClientOptions = {}) => {
    const client = axios.create({
        baseURL: getAPIUrl(baseType),
        headers: {
            // Set auth headers, so we don't need to pass them in every request
            ...auth.getHeaders().headers,
            ...(baseType === BaseTypes.BFF && {
                'x-roles': auth.getUser()?.roles?.toString(),
            }),
        },
    });
    AllClients.push(client);

    client.interceptors.response.use(
        (response) => response,
        (error) => {
            const { status } = error.response;
            // Handle unathorized requests
            if (status === StatusCodes.UNAUTHORIZED) {
                const originalRequest = error.config;
                console.log('expired session', auth.getHeaders().headers); // TODO: Remove when confirmed working
                // This client instance hasn't made an Auth token refresh request
                if (!isRefreshing) {
                    /**
                     * Another client instance has completed the refresh request
                     * this client just needs to update it's version of the auth token and reattempt the request.
                     **/
                    if (
                        auth.getHeaders().headers['Authorization'] !=
                        originalRequest.headers['Authorization']
                    ) {
                        client.defaults.headers = auth.getHeaders().headers;
                        originalRequest.headers['Authorization'] =
                            auth.getHeaders().headers['Authorization'];
                        return axios.request(originalRequest);
                    }
                    // This client is the first client to 401 and is therefore responsible for managing the refresh
                    isRefreshing = true;
                    return auth.initialize().then(() => {
                        console.log('re-initialize', auth.getHeaders().headers); // TODO: Remove when confirmed working
                        client.defaults.headers = auth.getHeaders().headers;
                        isRefreshing = false;
                        onRrefreshed(
                            auth.getHeaders().headers['Authorization'],
                        );
                    });
                }
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token: string) => {
                        // replace the expired token and retry request
                        originalRequest.headers['Authorization'] = token;
                        resolve(axios(originalRequest));
                    });
                });
            } else {
                return Promise.reject(error);
            }
        },
    );
    return client;
};

export default axiosClient;
